import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { AccessModes, Dimensions, Metrics, SortOrder, Sources, WITH_ID_SUFFIX } from 'components/insights/constants';
import MetricTile from 'components/insights/layout/tiles/MetricTile';
import SegmentedControl from 'components/shared/SegmentedControl';
import { colors } from 'styles/theme/colors';

import useNetworkPerformanceAnalytics from '../query/useNetworkPerformanceAnalytics';

const OES_VIEW = {
  MOST_ENGAGED: 'mostEngaged',
  LEAST_ENGAGED: 'leastEngaged',
} as const;

type OESViewValue = (typeof OES_VIEW)[keyof typeof OES_VIEW];

const MAX_BARS = 4;
const TRACK_COLOR = colors.black05;

const getOESColor = (score: number) => {
  if (score >= 100) return colors.accentGreen;
  if (score >= 75) return colors.accentYellow;
  return colors.accentRed;
};

export default function OESOverviewTile() {
  const [view, setView] = useState<OESViewValue>(OES_VIEW.MOST_ENGAGED);

  const queryParams = useMemo(
    () => ({
      source: Sources.LOCATION_EPISODE_DISCHARGES,
      accessModes: [AccessModes.MANAGER],
      dimensions: [`${Dimensions.PROVIDER_CLIENT}${WITH_ID_SUFFIX}`],
      metrics: [Metrics.OLIO_ENGAGEMENT_SCORE],
      rollups: true,
      sortBy: `${Metrics.OLIO_ENGAGEMENT_SCORE} ${SortOrder.DESC}`,
    }),
    []
  );

  const { loading, rollups } = useNetworkPerformanceAnalytics(queryParams, {
    queryKey: 'networkPerformanceOES',
  });

  const overallOES = rollups.overall.metrics.getCurrentDataPoint(Metrics.OLIO_ENGAGEMENT_SCORE, 0);

  const sortedData = useMemo(() => {
    const entityGrouping = rollups.groupings[1];
    const entityNames = entityGrouping?.dimensions.getCurrentDimensionValuesByIndex(0) ?? [];
    const entityScores = entityGrouping?.metrics.getCurrentSeries(Metrics.OLIO_ENGAGEMENT_SCORE) ?? [];

    const pairs = entityNames.map((name, i) => ({ name, score: entityScores[i] ?? 0 }));

    const sorted =
      view === OES_VIEW.MOST_ENGAGED
        ? [...pairs].sort((a, b) => b.score - a.score)
        : [...pairs].sort((a, b) => a.score - b.score);

    return sorted.slice(0, MAX_BARS);
  }, [rollups, view]);

  const maxScore = useMemo(() => {
    if (sortedData.length === 0) return 100;
    return Math.max(...sortedData.map((d) => d.score), 100);
  }, [sortedData]);

  const hasData = overallOES != null;

  const viewOptions = [
    { label: 'Most Engaged', value: OES_VIEW.MOST_ENGAGED },
    { label: 'Least Engaged', value: OES_VIEW.LEAST_ENGAGED },
  ];

  return (
    <MetricTile
      label='Olio Engagement Score (OES)'
      tooltipText='Measurement of engagement activity in Olio'
      value={hasData ? `${overallOES}%` : undefined}
      loading={loading}
      hasData={hasData}>
      <CompactToggle>
        <SegmentedControl options={viewOptions} selectedValue={view} onChange={(val) => setView(val as OESViewValue)} />
      </CompactToggle>
      <BarList>
        {sortedData.map((d) => (
          <BarRow key={d.name}>
            <BarLabel title={d.name}>{d.name}</BarLabel>
            <BarTrack>
              <BarFill
                style={{
                  width: `${Math.min((d.score / maxScore) * 100, 100)}%`,
                  backgroundColor: getOESColor(d.score),
                }}
              />
            </BarTrack>
            <BarValue>{d.score}%</BarValue>
          </BarRow>
        ))}
      </BarList>
    </MetricTile>
  );
}

const CompactToggle = styled.div`
  margin: 9px 0 16px;

  > div {
    display: flex;
    width: 100%;
    height: 24px;
    padding: 0 2px;
  }

  button {
    flex: 1;
    height: 20px;
    font-size: 9px;
    padding: 0 8px;
  }
`;

const BarList = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  justify-content: space-evenly;
`;

const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BarLabel = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.black};
  width: 100px;
  min-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BarTrack = styled.div`
  flex: 1;
  height: 10px;
  background-color: ${TRACK_COLOR};
  border-radius: 2px;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease;
`;

const BarValue = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.black};
  width: 36px;
  min-width: 36px;
  text-align: right;
`;
